import React, { useEffect, useRef, useState } from "react";
import { Modal, Nav, Navbar } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import styled, { css, keyframes } from "styled-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LuArrowRight,
  LuBellRing,
  LuBuilding2,
  LuCalendarCheck,
  LuCheck,
  LuCheckCircle2,
  LuClipboardList,
  LuClock,
  LuCreditCard,
  LuGlobe,
  LuLayoutGrid,
  LuLink,
  LuMail,
  LuMessageCircle,
  LuMessagesSquare,
  LuMonitor,
  LuPlay,
  LuPlugZap,
  LuReceipt,
  LuUsers,
  LuWand2,
  LuX,
  LuZap,
} from "react-icons/lu";

import FormContainer from "../components/FormContainer";
import LanguageSelector from "../components/LanguageSelector";
import CreateAccountScreen from "./CreateAccountScreen";
import AccountRecovery from "./AccountRecovery";
import PasswordRecoveryChange from "./PasswordRecoveryChange";
import { fetchReservationActive } from "../actions/reservationsActions";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { supportedLanguages } from "../i18n";

const YOUTUBE_ID = "aBlNolvv-s8";
// youtube-nocookie is YouTube's privacy-enhanced player. It is only loaded
// once someone presses play; until then the page shows the poster image.
const VIDEO_EMBED = `https://www.youtube-nocookie.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0`;
const VIDEO_POSTER = `https://i.ytimg.com/vi/${YOUTUBE_ID}/maxresdefault.jpg`;
const VIDEO_POSTER_FALLBACK = `https://i.ytimg.com/vi/${YOUTUBE_ID}/hqdefault.jpg`;

const ORANGE = "#ee754c";
const ORANGE_DARK = "#d8501f";
const INK = "#2b2522";
const BODY = "#5f5a57";
const MUTED = "#8c8580";
const LINE = "#f0e4dd";
const TITLE_FONT = `"Barlow Condensed", var(--app-font)`;
const HUB_REACH = 72;

/* ---------------- Content ---------------- */
// Ordered so the three-column grid balances: the longest lists share the top
// rows and the shortest sits beside the two-column closing card.
const FEATURE_GROUPS = [
  { id: "booking", icon: LuCalendarCheck },
  { id: "floor", icon: LuLayoutGrid },
  { id: "communication", icon: LuMessagesSquare },
  { id: "daily", icon: LuClipboardList },
  { id: "schedule", icon: LuClock },
  { id: "service", icon: LuZap },
  { id: "team", icon: LuBuilding2 },
  { id: "integrations", icon: LuPlugZap },
  { id: "alerts", icon: LuBellRing },
  { id: "customers", icon: LuUsers },
];

const STEPS = [
  { id: "setup", icon: LuWand2 },
  { id: "share", icon: LuLink },
  { id: "handled", icon: LuCheckCircle2 },
];

// Partners are named in text with an icon for what each one does. Naming a
// product to say Soup works with it is nominative use; reproducing its logo
// would need the brand's permission (same rule as the email campaigns).
const BUSINESS_NODES = [
  { id: "zonesoft", name: "Zonesoft", icon: LuMonitor },
  { id: "moloni", name: "Moloni", icon: LuReceipt },
  { id: "stripe", name: "Stripe", icon: LuCreditCard },
];
const GUEST_NODES = [
  { id: "whatsapp", name: "WhatsApp", icon: LuMessageCircle },
  { id: "email", icon: LuMail },
  { id: "website", icon: LuGlobe },
];

const PLAN_COLORS = [
  { accent: "#9FBBC1", ink: "#1f3438" },
  { accent: "#EE754C", ink: "#ffffff" },
  { accent: "#99B0AD", ink: "#1f302c" },
];
const getPlanColor = (n) =>
  PLAN_COLORS[(Number(n) - 1) % PLAN_COLORS.length] || PLAN_COLORS[1];

/* ---------- Helpers for plan offers (semicolon-only) ---------- */
const splitOffersSafely = (s) =>
  (s || "")
    .split(/\s*;\s*/g) // ONLY semicolons between items
    .map((x) => x.trim())
    .filter(Boolean);

const splitTitleAndDesc = (item) => {
  const idx = item.indexOf(":");
  if (idx === -1) return { title: item.trim(), desc: "" };
  return { title: item.slice(0, idx).trim(), desc: item.slice(idx + 1).trim() };
};

const asList = (value) => (Array.isArray(value) ? value : []);

// Translations mark emphasis as **text**; split it out rather than inject HTML.
const Emphasised = ({ text }) =>
  String(text)
    .split(/\*\*(.+?)\*\*/g)
    .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

/* ---------------- Motion ---------------- */
const flow = keyframes`from { background-position: 0 0; } to { background-position: 12px 0; }`;
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(238, 117, 76, 0.45); }
  100% { box-shadow: 0 0 0 22px rgba(238, 117, 76, 0); }
`;
const float = keyframes`0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); }`;

/* ---------------- Shared pieces ---------------- */
const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: ${({ $small }) => ($small ? "38px" : "48px")};
  padding: ${({ $small }) => ($small ? "8px 16px" : "12px 22px")};
  border-radius: 999px;
  font-size: ${({ $small }) => ($small ? "14px" : "15px")};
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;

  svg {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &:focus-visible {
    outline: 3px solid rgba(238, 117, 76, 0.4);
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled.button`
  ${buttonBase};
  border: 0;
  color: ${({ $inverse }) => ($inverse ? ORANGE_DARK : "#fff")};
  background: ${({ $inverse }) => ($inverse ? "#fff" : ORANGE)};
  box-shadow: ${({ $inverse, $small }) =>
    $inverse || $small ? "none" : "0 10px 24px -10px rgba(238, 117, 76, 0.75)"};

  &:hover {
    color: ${({ $inverse }) => ($inverse ? ORANGE_DARK : "#fff")};
    background: ${({ $inverse }) => ($inverse ? "#fff4ee" : ORANGE_DARK)};
    transform: translateY(-1px);
  }

  &:hover svg {
    transform: translateX(3px);
  }
`;

const GhostButton = styled.button`
  ${buttonBase};
  border: 1px solid
    ${({ $onDark }) => ($onDark ? "rgba(255, 255, 255, 0.75)" : "#ecd9cf")};
  color: ${({ $onDark }) => ($onDark ? "#fff" : INK)};
  background: ${({ $onDark }) => ($onDark ? "transparent" : "#fff")};

  &:hover {
    color: ${ORANGE_DARK};
    border-color: ${({ $onDark }) => ($onDark ? "#fff" : "#f3b79d")};
    background: ${({ $onDark }) => ($onDark ? "#fff" : "#fff4ee")};
  }
`;

const PlayDot = styled.span`
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  margin-left: -8px;
  border-radius: 50%;
  color: #fff;
  background: ${ORANGE};

  svg {
    width: 11px;
    height: 11px;
    margin-left: 2px;
    fill: currentColor;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
`;

const IconTile = styled.span`
  display: inline-grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  color: ${ORANGE_DARK};
  background: linear-gradient(135deg, #fff3ec 0%, #ffdfcf 100%);
  box-shadow: inset 0 0 0 1px rgba(238, 117, 76, 0.18);

  svg {
    width: 23px;
    height: 23px;
  }
`;

const Section = styled.section`
  padding: clamp(64px, 9vw, 112px) clamp(16px, 4vw, 48px);
  background: ${({ $tint }) => ($tint ? "#fbf7f4" : "#fff")};
  scroll-margin-top: 64px;
  ${({ $flushTop }) =>
    $flushTop &&
    css`
      padding-top: 0;
    `}
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHead = styled.header`
  max-width: ${({ $align }) => ($align === "left" ? "none" : "720px")};
  margin: ${({ $align }) =>
    $align === "left" ? "0" : "0 auto clamp(36px, 5vw, 56px)"};
  text-align: ${({ $align }) => ($align === "left" ? "left" : "center")};

  > span {
    display: block;
    margin-bottom: 10px;
    color: ${ORANGE};
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  h2 {
    margin: 0 0 14px;
    font-family: ${TITLE_FONT};
    font-size: clamp(2.2rem, 4.5vw, 3.4rem);
    font-weight: 600;
    line-height: 1.02;
    color: ${({ $dark }) => ($dark ? "#fff" : INK)};
  }

  p {
    margin: 0;
    font-size: 17px;
    line-height: 1.6;
    color: ${({ $dark }) => ($dark ? "#cbbdb5" : BODY)};
  }
`;

/* ---------------- Navigation ---------------- */
const Page = styled.div`
  color: ${INK};
  background: #fff;
`;

const TopBar = styled(Navbar)`
  position: sticky;
  top: 0;
  z-index: 1050;
  padding: 10px clamp(16px, 4vw, 48px);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: saturate(160%) blur(10px);
  border-bottom: 1px solid rgba(238, 117, 76, 0.12);
`;

const Brand = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;

  img {
    width: 92px;
    height: auto;
  }
`;

const MenuToggle = styled(Navbar.Toggle)`
  padding: 12px 4px;
  border: none !important;
  box-shadow: none !important;

  .navbar-toggler-icon {
    position: relative;
    display: block;
    width: 22px;
    height: 2px;
    background: ${INK};
    background-image: none;
  }

  .navbar-toggler-icon::before,
  .navbar-toggler-icon::after {
    content: "";
    position: absolute;
    left: 0;
    width: 22px;
    height: 2px;
    background: ${INK};
  }

  .navbar-toggler-icon::before {
    top: -7px;
  }

  .navbar-toggler-icon::after {
    top: 7px;
  }
`;

const NavLinks = styled(Nav)`
  align-items: center;
  gap: 2px;

  @media (max-width: 991px) {
    align-items: stretch;
    padding-top: 12px;
  }
`;

const NavLink = styled.button`
  padding: 8px 12px;
  border: 0;
  border-radius: 999px;
  color: ${BODY};
  background: none;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  transition:
    color 0.2s ease,
    background-color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: ${ORANGE_DARK};
    background: #fff4ee;
    outline: none;
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 20px;

  @media (max-width: 991px) {
    flex-wrap: wrap;
    margin: 12px 0 8px;
  }
`;

/* ---------------- Hero ---------------- */
const Hero = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(40px, 7vw, 88px) clamp(16px, 4vw, 48px)
    clamp(56px, 7vw, 96px);
  background:
    radial-gradient(55% 60% at 88% 8%, rgba(238, 117, 76, 0.18), transparent 70%),
    radial-gradient(45% 50% at 0% 100%, rgba(153, 176, 173, 0.25), transparent 70%),
    linear-gradient(180deg, #fff6f0 0%, #ffffff 100%);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  align-items: start;
  gap: clamp(32px, 5vw, 72px);
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 991px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const HeroCopy = styled.div`
  @media (min-width: 992px) {
    padding-top: 40px;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px 6px 10px;
  border: 1px solid #f5d9cb;
  border-radius: 999px;
  color: ${ORANGE_DARK};
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${ORANGE};
    box-shadow: 0 0 0 4px rgba(238, 117, 76, 0.18);
  }
`;

const HeroTitle = styled.h1`
  margin: 22px 0 20px;
  font-family: ${TITLE_FONT};
  font-size: clamp(2.8rem, 6vw, 4.6rem);
  font-weight: 600;
  line-height: 0.98;
  letter-spacing: -0.01em;
  color: ${INK};

  span {
    display: block;
    color: ${ORANGE};
  }
`;

const Lead = styled.p`
  max-width: 34em;
  margin: 0 0 30px;
  font-size: clamp(16px, 1.6vw, 18px);
  line-height: 1.65;
  color: ${BODY};
`;

const Stats = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 40px;
  margin: 40px 0 0;
  padding-top: 24px;
  border-top: 1px solid #f3dfd4;

  div {
    display: flex;
    flex-direction: column-reverse;
  }

  dt {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 400;
    color: ${MUTED};
  }

  dd {
    margin: 0;
    font-family: ${TITLE_FONT};
    font-size: 36px;
    font-weight: 600;
    line-height: 1;
    color: ${INK};
  }
`;

const AuthCard = styled.div`
  position: relative;
  justify-self: end;
  width: 100%;
  max-width: 460px;
  padding: clamp(24px, 3vw, 36px);
  border: 1px solid #f3e3da;
  border-radius: 24px;
  background: #fff;
  box-shadow:
    0 30px 60px -30px rgba(120, 60, 30, 0.35),
    0 2px 6px rgba(0, 0, 0, 0.04);

  > img {
    display: block;
    width: 140px;
    height: auto;
    margin: 0 auto 6px;
  }

  /* FormContainer sizes itself at 120% inline, which overflows a card. */
  .container-fluid {
    width: 100% !important;
    max-width: 100% !important;
  }

  @media (max-width: 991px) {
    justify-self: center;
    ${({ $first }) =>
      $first &&
      css`
        order: -1;
      `}
  }
`;

const SubscribeNotice = styled.div`
  margin: 10px 0 16px;
  padding: 10px 20px;
  border-radius: 10px;
  color: #b91c1c;
  background: #fee2e2;
  font-weight: 600;
  text-align: center;
`;

const FloatingNote = styled.div`
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 10px;
  border: 1px solid #f3e3da;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 16px 36px -14px rgba(90, 45, 25, 0.35);
  white-space: nowrap;
  pointer-events: none;
  animation: ${float} 5s ease-in-out infinite;
  ${({ $pos }) =>
    $pos === "top"
      ? css`
          top: -26px;
          left: -60px;
        `
      : css`
          right: 28px;
          bottom: -26px;
          animation-delay: -2.5s;
        `}

  > span {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    color: ${({ $tone }) => ($tone === "sage" ? "#3f6b62" : ORANGE_DARK)};
    background: ${({ $tone }) => ($tone === "sage" ? "#e3ecea" : "#ffe9df")};
  }

  strong {
    display: block;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.25;
    color: ${INK};
  }

  small {
    display: block;
    font-size: 12px;
    color: ${MUTED};
  }

  @media (max-width: 1180px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ---------------- Steps ---------------- */
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Step = styled.li`
  position: relative;
  padding: 28px;
  border: 1px solid ${LINE};
  border-radius: 20px;
  background: #fff;
  counter-increment: step;

  &::after {
    content: "0" counter(step);
    position: absolute;
    top: 18px;
    right: 24px;
    font-family: ${TITLE_FONT};
    font-size: 48px;
    font-weight: 600;
    line-height: 1;
    color: #f7e0d5;
  }

  h3 {
    margin: 18px 0 8px;
    font-size: 18px;
    font-weight: 600;
    color: ${INK};
  }

  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: ${BODY};
  }
`;

/* ---------------- Features ---------------- */
const FeatureGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (max-width: 991px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const FeatureCard = styled.li`
  display: flex;
  flex-direction: column;
  padding: 26px;
  border: 1px solid ${LINE};
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(60, 30, 20, 0.04);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #f3cdb9;
    box-shadow: 0 22px 44px -26px rgba(120, 60, 30, 0.4);
  }

  h3 {
    margin: 18px 0 4px;
    font-size: 18px;
    font-weight: 600;
    color: ${INK};
  }

  > p {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.5;
    color: ${MUTED};
  }

  ul {
    display: grid;
    gap: 9px;
    margin: 0;
    padding: 16px 0 0;
    border-top: 1px dashed #efd9cd;
    list-style: none;
  }

  li {
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr);
    gap: 10px;
    font-size: 14px;
    line-height: 1.5;
    color: ${BODY};
  }

  li svg {
    width: 16px;
    height: 16px;
    margin-top: 3px;
    color: ${ORANGE};
  }

  strong {
    font-weight: 600;
    color: ${INK};
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const MoreCard = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  grid-column: span 2;
  padding: clamp(28px, 4vw, 44px);
  border-radius: 20px;
  color: #fff;
  background:
    radial-gradient(120% 140% at 100% 0%, #ffb08f 0%, transparent 55%),
    linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DARK} 100%);

  h3 {
    margin: 0;
    font-family: ${TITLE_FONT};
    font-size: clamp(30px, 3.2vw, 40px);
    font-weight: 600;
    line-height: 1.02;
  }

  p {
    max-width: 36em;
    margin: 0 0 6px;
    font-size: 15px;
    line-height: 1.6;
    color: #fff1ea;
  }

  @media (max-width: 600px) {
    grid-column: auto;
  }
`;

/* ---------------- Demo ---------------- */
const DemoGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  align-items: center;
  gap: clamp(32px, 5vw, 64px);

  @media (max-width: 991px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const BrowserFrame = styled.div`
  overflow: hidden;
  border: 1px solid #eadfd8;
  border-radius: 18px;
  background: #fff;
  box-shadow:
    0 40px 80px -40px rgba(90, 45, 25, 0.45),
    0 2px 8px rgba(0, 0, 0, 0.04);
`;

const FrameBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-bottom: 1px solid #efe5df;
  background: #faf6f3;

  i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #f3c7b4;
  }

  i:nth-child(2) {
    background: #f6dccd;
  }

  i:nth-child(3) {
    background: #dde8e5;
  }

  span {
    display: flex;
    align-items: center;
    flex: 1;
    max-width: 240px;
    height: 22px;
    margin-left: 12px;
    padding: 0 10px;
    border: 1px solid #efe5df;
    border-radius: 6px;
    background: #fff;
    color: #a09189;
    font-size: 11px;
  }
`;

const VideoFrame = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: #1f1714;

  iframe,
  > button {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const PlayDisc = styled.span`
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  display: grid;
  place-items: center;
  width: 76px;
  height: 76px;
  margin: -38px 0 0 -38px;
  border-radius: 50%;
  color: #fff;
  background: ${ORANGE};
  box-shadow:
    0 0 0 10px rgba(238, 117, 76, 0.25),
    0 16px 40px -10px rgba(0, 0, 0, 0.5);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  svg {
    width: 30px;
    height: 30px;
    margin-left: 4px;
    fill: currentColor;
  }

  @media (max-width: 600px) {
    width: 60px;
    height: 60px;
    margin: -30px 0 0 -30px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const PlayCover = styled.button`
  display: block;
  padding: 0;
  background: #1f1714;
  cursor: pointer;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition:
      transform 0.4s ease,
      filter 0.3s ease;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(20, 12, 9, 0) 45%, rgba(20, 12, 9, 0.7) 100%);
  }

  > span:last-child {
    position: absolute;
    z-index: 1;
    right: 20px;
    bottom: 16px;
    left: 20px;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    text-align: left;
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
  }

  &:hover img {
    transform: scale(1.03);
    filter: brightness(0.9);
  }

  &:hover ${PlayDisc} {
    transform: scale(1.06);
    box-shadow:
      0 0 0 14px rgba(238, 117, 76, 0.3),
      0 16px 40px -10px rgba(0, 0, 0, 0.5);
  }

  &:focus-visible {
    outline: 3px solid rgba(238, 117, 76, 0.7);
    outline-offset: -3px;
  }

  @media (max-width: 600px) {
    > span:last-child {
      right: 14px;
      bottom: 10px;
      left: 14px;
      font-size: 13px;
    }
  }
`;

const VideoModal = styled(Modal)`
  .modal-dialog {
    max-width: min(1100px, calc(100vw - 32px));
  }

  .modal-content {
    position: relative;
    overflow: visible;
    border: 0;
    border-radius: 18px;
    background: transparent;
    box-shadow: none;
  }

  ${VideoFrame} {
    border-radius: 18px;
    box-shadow: 0 40px 120px -20px rgba(0, 0, 0, 0.6);
  }
`;

const CloseVideo = styled.button`
  position: absolute;
  top: -52px;
  right: 0;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  color: ${INK};
  background: #fff;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: ${ORANGE_DARK};
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.6);
    outline-offset: 2px;
  }
`;

const VideoPlayer = ({ title, playLabel, autoPlay = false }) => {
  const [playing, setPlaying] = useState(autoPlay);
  return (
    <VideoFrame>
      {playing ? (
        <iframe
          src={VIDEO_EMBED}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <PlayCover
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`${playLabel}: ${title}`}
        >
          <img
            src={VIDEO_POSTER}
            alt=""
            loading="lazy"
            onError={(event) => {
              if (event.currentTarget.src !== VIDEO_POSTER_FALLBACK) {
                event.currentTarget.src = VIDEO_POSTER_FALLBACK;
              }
            }}
          />
          <PlayDisc aria-hidden="true">
            <LuPlay />
          </PlayDisc>
          <span>{title}</span>
        </PlayCover>
      )}
    </VideoFrame>
  );
};

/* ---------------- Partners ---------------- */
const DarkSection = styled(Section)`
  position: relative;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(70% 55% at 50% 0%, rgba(238, 117, 76, 0.22) 0%, transparent 70%),
    #211916;
`;

const Hub = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  max-width: 1080px;
  margin: 0 auto;

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
  }
`;

const HubCore = styled.div`
  position: relative;
  align-self: stretch;
  display: grid;
  place-items: center;
  width: 190px;
  margin: 0 ${HUB_REACH}px;
  padding: 28px 20px;
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff 0%, #fff1ea 100%);
  box-shadow:
    0 0 0 1px rgba(238, 117, 76, 0.6),
    0 0 70px 6px rgba(238, 117, 76, 0.28);

  img {
    width: 116px;
    height: auto;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    animation: ${pulse} 2.6s ease-out infinite;
  }

  @media (max-width: 860px) {
    order: -1;
    align-self: center;
    justify-self: center;
    width: auto;
    margin: 0;
    padding: 22px 40px;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

const ColumnLabel = styled.span`
  display: block;
  margin-bottom: 14px;
  color: #a08e85;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-align: ${({ $side }) => ($side === "left" ? "right" : "left")};
  text-transform: uppercase;

  @media (max-width: 860px) {
    text-align: left;
  }
`;

const NodeList = styled.ul`
  display: grid;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (max-width: 860px) {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
`;

const Node = styled.li`
  position: relative;
  display: flex;
  flex-direction: ${({ $side }) => ($side === "left" ? "row-reverse" : "row")};
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  text-align: ${({ $side }) => ($side === "left" ? "right" : "left")};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    border-color: rgba(238, 117, 76, 0.55);
    background: rgba(238, 117, 76, 0.08);
  }

  strong {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
  }

  div > span {
    display: block;
    font-size: 13px;
    color: #b8a79e;
  }

  /* Dashed wire into the hub; both sides flow left to right, so the page reads
     as business systems -> Soup -> guests. */
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    ${({ $side }) => ($side === "left" ? "right" : "left")}: -${HUB_REACH + 1}px;
    width: ${HUB_REACH}px;
    height: 2px;
    margin-top: -1px;
    background-image: linear-gradient(90deg, ${ORANGE} 50%, transparent 50%);
    background-size: 12px 2px;
    opacity: 0.85;
    animation: ${flow} 0.9s linear infinite;
  }

  @media (max-width: 860px) {
    flex-direction: row;
    text-align: left;

    &::after {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const NodeIcon = styled.span`
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: #ffab8a;
  background: rgba(238, 117, 76, 0.14);

  svg {
    width: 20px;
    height: 20px;
  }
`;

/* ---------------- Plans ---------------- */
const PlanToggle = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 40px;

  > div {
    display: inline-flex;
    padding: 4px;
    border-radius: 999px;
    background: #f6efeb;
  }

  button {
    padding: 9px 24px;
    border: 0;
    border-radius: 999px;
    color: ${BODY};
    background: transparent;
    font-size: 14px;
    font-weight: 600;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
  }

  button[aria-pressed="true"] {
    color: ${ORANGE_DARK};
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 340px));
  justify-content: center;
  gap: 20px;
`;

const PlanCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 34px 28px 28px;
  border: 1px solid ${LINE};
  border-radius: 24px;
  background: #fff;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto;
    height: 5px;
    background: ${({ $color }) => $color.accent};
  }

  h3 {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${INK};
  }
`;

const PlanDescription = styled.p`
  margin: 0 0 22px;
  font-size: 14px;
  color: ${MUTED};
`;

const PlanPrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;

  strong {
    font-family: ${TITLE_FONT};
    font-size: 54px;
    font-weight: 600;
    line-height: 1;
    color: ${INK};
  }

  span {
    font-size: 14px;
    color: ${MUTED};
  }
`;

const DiscountNote = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #3f8f5a;
`;

const PlanButton = styled.button`
  ${buttonBase};
  width: 100%;
  margin: 24px 0;
  border: 0;
  color: ${({ $color }) => $color.ink};
  background: ${({ $color }) => $color.accent};

  &:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
  }
`;

const PlanOffers = styled.ul`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 20px 0 0;
  border-top: 1px solid ${LINE};
  list-style: none;

  li {
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr);
    gap: 10px;
    font-size: 14px;
    line-height: 1.5;
    color: ${BODY};
  }

  svg {
    width: 16px;
    height: 16px;
    margin-top: 3px;
    color: ${ORANGE};
  }

  strong {
    display: block;
    font-weight: 600;
    color: ${INK};
  }
`;

/* ---------------- Closing call to action ---------------- */
const CtaCard = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(40px, 6vw, 72px) clamp(20px, 4vw, 48px);
  border: 1px solid #f5dccf;
  border-radius: 28px;
  background:
    radial-gradient(70% 120% at 0% 0%, #ffe1d2 0%, transparent 60%),
    radial-gradient(60% 120% at 100% 100%, #dfe9e6 0%, transparent 60%),
    #fff7f2;
  text-align: center;

  h2 {
    margin: 0 0 12px;
    font-family: ${TITLE_FONT};
    font-size: clamp(2.2rem, 4.5vw, 3.4rem);
    font-weight: 600;
    line-height: 1.02;
    color: ${INK};
  }

  p {
    margin: 0 0 28px;
    font-size: 17px;
    color: ${BODY};
  }
`;

/* ---------------- Component ---------------- */
const LoginScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const demoRef = useRef(null);
  const featuresRef = useRef(null);
  const partnersRef = useRef(null);
  const planosRef = useRef(null);
  const topRef = useRef(null);

  const { recovery_code } = useParams();
  const [showRegister, setShowRegister] = useState(false);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [showMonthly, setShowMonthly] = useState(true);
  const [showSubscribeNotice, setShowSubscribeNotice] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  // On one column the card normally follows the headline; once someone asks
  // for it (or arrives from a recovery link) it goes above the headline.
  const [authFirst, setAuthFirst] = useState(Boolean(recovery_code));

  const { getSubscriptionPackages } = useSelector(
    (state) => state.subscriptionPackagesStore,
  );

  // robust buckets (so 28–31 days still counts as monthly, etc.)
  const monthlyPlans =
    getSubscriptionPackages
      ?.filter(
        (p) => Number(p.number_of_days) > 0 && Number(p.number_of_days) < 90,
      )
      .sort((a, b) => a.subscription_number - b.subscription_number) || [];
  const yearlyPlans =
    getSubscriptionPackages
      ?.filter((p) => Number(p.number_of_days) >= 300)
      .sort((a, b) => a.subscription_number - b.subscription_number) || [];

  useEffect(() => {
    dispatch(fetchSubscriptionPackages());
  }, [dispatch]);
  useEffect(() => {
    dispatch(fetchReservationActive());
  }, [dispatch]);


  const groupItems = (id) =>
    asList(
      t(`loginScreen.features.groups.${id}.items`, { returnObjects: true }),
    );
  const featureCount = FEATURE_GROUPS.reduce(
    (total, { id }) => total + groupItems(id).length,
    0,
  );

  // A smooth scroll begun from the phone menu stalls partway, so from there the
  // page jumps instead, once the menu has finished closing (see onExited).
  const pendingScroll = useRef(null);
  const scrollToSection = (ref) => {
    if (showMenu) {
      pendingScroll.current = () => ref.current?.scrollIntoView();
      setShowMenu(false);
    } else {
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }
  };
  const runPendingScroll = () => {
    pendingScroll.current?.();
    pendingScroll.current = null;
  };

  // Asking for the form brings it to the top of the page instead of sending
  // anyone down to it: beside the headline it is already there, and on one
  // column the card moves above the headline.
  const openAuth = (register) => {
    setShowPasswordRecovery(false);
    setShowRegister(register);
    setAuthFirst(true);
    scrollToSection(topRef);
  };

  const handleSubscribeClick = () => {
    setShowSubscribeNotice(true);
    openAuth(false);
  };

  const navSections = [
    { key: "features", ref: featuresRef },
    { key: "demo", ref: demoRef },
    { key: "partners", ref: partnersRef },
    ...(getSubscriptionPackages ? [{ key: "plans", ref: planosRef }] : []),
  ];

  const renderNodes = (nodes, side) => (
    <NodeList>
      {nodes.map(({ id, name, icon: Icon }) => (
        <Node key={id} $side={side}>
          <NodeIcon aria-hidden="true">
            <Icon />
          </NodeIcon>
          <div>
            <strong>{name || t(`loginScreen.partners.names.${id}`)}</strong>
            <span>{t(`loginScreen.partners.roles.${id}`)}</span>
          </div>
        </Node>
      ))}
    </NodeList>
  );

  const videoButton = (label, onDark) => (
    <GhostButton
      type="button"
      onClick={() => setShowVideo(true)}
      $onDark={onDark}
    >
      {onDark ? (
        <LuPlay aria-hidden="true" />
      ) : (
        <PlayDot aria-hidden="true">
          <LuPlay />
        </PlayDot>
      )}
      {label}
    </GhostButton>
  );

  return (
    <Page>
      <div ref={topRef} />
      <TopBar expand="lg" expanded={showMenu} onToggle={setShowMenu}>
        <Brand
          type="button"
          onClick={() => scrollToSection(topRef)}
          aria-label={t("loginScreen.nav.home")}
        >
          <img src="/static/logo.png" alt={t("loginScreen.alt.logo")} />
        </Brand>

        <MenuToggle
          aria-controls="landing-nav"
          aria-label={t("loginScreen.nav.menu")}
        >
          <span className="navbar-toggler-icon" />
        </MenuToggle>

        <Navbar.Collapse id="landing-nav" onExited={runPendingScroll}>
          <NavLinks className="ms-auto">
            {navSections.map(({ key, ref }) => (
              <NavLink key={key} type="button" onClick={() => scrollToSection(ref)}>
                {t(`loginScreen.nav.${key}`)}
              </NavLink>
            ))}
          </NavLinks>
          <NavActions>
            <LanguageSelector />
            <NavLink type="button" onClick={() => openAuth(false)}>
              {t("auth.login")}
            </NavLink>
            <PrimaryButton type="button" $small onClick={() => openAuth(true)}>
              {t("loginScreen.nav.register")}
            </PrimaryButton>
          </NavActions>
        </Navbar.Collapse>
      </TopBar>

      <Hero>
        <HeroGrid>
          <HeroCopy>
            <Eyebrow>{t("loginScreen.hero.eyebrow")}</Eyebrow>
            <HeroTitle>
              {t("loginScreen.hero.titleLead")}{" "}
              <span>{t("loginScreen.hero.titleAccent")}</span>
            </HeroTitle>
            <Lead>{t("loginScreen.hero.lead")}</Lead>
            <ButtonRow>
              <PrimaryButton type="button" onClick={() => openAuth(true)}>
                {t("loginScreen.hero.ctaPrimary")}
                <LuArrowRight aria-hidden="true" />
              </PrimaryButton>
              {videoButton(t("loginScreen.hero.ctaVideo"))}
            </ButtonRow>
            <Stats>
              <div>
                <dt>{t("loginScreen.hero.stats.hoursLabel")}</dt>
                <dd>{t("loginScreen.hero.stats.hoursValue")}</dd>
              </div>
              <div>
                <dt>{t("loginScreen.hero.stats.featuresLabel")}</dt>
                <dd>{featureCount}</dd>
              </div>
              <div>
                <dt>
                  {t("loginScreen.hero.stats.languagesLabel", {
                    list: supportedLanguages.join(" · ").toUpperCase(),
                  })}
                </dt>
                <dd>{supportedLanguages.length}</dd>
              </div>
            </Stats>
          </HeroCopy>

          <AuthCard $first={authFirst}>
            <FloatingNote $pos="top" aria-hidden="true">
              <span>
                <LuCheckCircle2 />
              </span>
              <div>
                <strong>{t("loginScreen.hero.notes.acceptedTitle")}</strong>
                <small>{t("loginScreen.hero.notes.acceptedDetail")}</small>
              </div>
            </FloatingNote>
            <FloatingNote $pos="bottom" $tone="sage" aria-hidden="true">
              <span>
                <LuMessageCircle />
              </span>
              <div>
                <strong>{t("loginScreen.hero.notes.reminderTitle")}</strong>
                <small>{t("loginScreen.hero.notes.reminderDetail")}</small>
              </div>
            </FloatingNote>

            <img src="/static/logo.png" alt={t("loginScreen.alt.companyLogo")} />
            {recovery_code ? (
              <PasswordRecoveryChange code={recovery_code} />
            ) : showRegister ? (
              <CreateAccountScreen
                onSwitchToLogin={() => setShowRegister(false)}
              />
            ) : showPasswordRecovery ? (
              <AccountRecovery
                onSwitchToLogin={() => setShowPasswordRecovery(false)}
              />
            ) : (
              <>
                {showSubscribeNotice && (
                  <SubscribeNotice role="status">
                    {t("loginScreen.auth.subscribeNotice")}
                  </SubscribeNotice>
                )}
                <FormContainer
                  onSwitchToRegister={() => setShowRegister(true)}
                  onSwitchToRecovery={() => setShowPasswordRecovery(true)}
                />
              </>
            )}
          </AuthCard>
        </HeroGrid>
      </Hero>

      <Section aria-labelledby="landing-steps">
        <Inner>
          <SectionHead>
            <span>{t("loginScreen.steps.eyebrow")}</span>
            <h2 id="landing-steps">{t("loginScreen.steps.title")}</h2>
          </SectionHead>
          <Steps>
            {STEPS.map(({ id, icon: Icon }) => (
              <Step key={id}>
                <IconTile aria-hidden="true">
                  <Icon />
                </IconTile>
                <h3>{t(`loginScreen.steps.items.${id}.title`)}</h3>
                <p>{t(`loginScreen.steps.items.${id}.text`)}</p>
              </Step>
            ))}
          </Steps>
        </Inner>
      </Section>

      <Section $tint ref={featuresRef} aria-labelledby="landing-features">
        <Inner>
          <SectionHead>
            <span>{t("loginScreen.features.eyebrow")}</span>
            <h2 id="landing-features">
              {t("loginScreen.features.title", { total: featureCount })}
            </h2>
            <p>{t("loginScreen.features.subtitle")}</p>
          </SectionHead>
          <FeatureGrid>
            {FEATURE_GROUPS.map(({ id, icon: Icon }) => (
              <FeatureCard key={id}>
                <IconTile aria-hidden="true">
                  <Icon />
                </IconTile>
                <h3>{t(`loginScreen.features.groups.${id}.title`)}</h3>
                <p>{t(`loginScreen.features.groups.${id}.tagline`)}</p>
                <ul>
                  {groupItems(id).map((item) => (
                    <li key={item}>
                      <LuCheck aria-hidden="true" />
                      <span>
                        <Emphasised text={item} />
                      </span>
                    </li>
                  ))}
                </ul>
              </FeatureCard>
            ))}
            <MoreCard>
              <h3>{t("loginScreen.features.more.title")}</h3>
              <p>{t("loginScreen.features.more.text")}</p>
              <ButtonRow>
                <PrimaryButton
                  type="button"
                  $inverse
                  onClick={() => openAuth(true)}
                >
                  {t("loginScreen.cta.primary")}
                  <LuArrowRight aria-hidden="true" />
                </PrimaryButton>
                {videoButton(t("loginScreen.cta.secondary"), true)}
              </ButtonRow>
            </MoreCard>
          </FeatureGrid>
        </Inner>
      </Section>

      <Section ref={demoRef} aria-labelledby="landing-demo">
        <Inner>
          <DemoGrid>
            <SectionHead $align="left">
              <span>{t("loginScreen.nav.demo")}</span>
              <h2 id="landing-demo">{t("loginScreen.demo.title")}</h2>
              <p>{t("loginScreen.demo.description")}</p>
            </SectionHead>
            <BrowserFrame>
              <FrameBar aria-hidden="true">
                <i />
                <i />
                <i />
                <span>soup.red</span>
              </FrameBar>
              <VideoPlayer
                title={t("loginScreen.video.title")}
                playLabel={t("loginScreen.video.play")}
              />
            </BrowserFrame>
          </DemoGrid>
        </Inner>
      </Section>

      <DarkSection ref={partnersRef} aria-labelledby="landing-partners">
        <Inner>
          <SectionHead $dark>
            <span>{t("loginScreen.partners.eyebrow")}</span>
            <h2 id="landing-partners">{t("loginScreen.partners.title")}</h2>
            <p>{t("loginScreen.partners.subtitle")}</p>
          </SectionHead>
          <Hub>
            <div>
              <ColumnLabel $side="left">
                {t("loginScreen.partners.business")}
              </ColumnLabel>
              {renderNodes(BUSINESS_NODES, "left")}
            </div>
            <HubCore>
              <img src="/static/logo.png" alt={t("loginScreen.alt.logo")} />
            </HubCore>
            <div>
              <ColumnLabel $side="right">
                {t("loginScreen.partners.guests")}
              </ColumnLabel>
              {renderNodes(GUEST_NODES, "right")}
            </div>
          </Hub>
        </Inner>
      </DarkSection>

      {getSubscriptionPackages && (
        <Section ref={planosRef} aria-labelledby="landing-plans">
          <Inner>
            <SectionHead>
              <span>{t("loginScreen.nav.plans")}</span>
              <h2 id="landing-plans">{t("loginScreen.plans.title")}</h2>
            </SectionHead>

            <PlanToggle>
              <div role="group" aria-label={t("loginScreen.plans.title")}>
                <button
                  type="button"
                  aria-pressed={showMonthly}
                  onClick={() => setShowMonthly(true)}
                >
                  {t("loginScreen.plans.monthly")}
                </button>
                <button
                  type="button"
                  aria-pressed={!showMonthly}
                  onClick={() => setShowMonthly(false)}
                >
                  {t("loginScreen.plans.yearly")}
                </button>
              </div>
            </PlanToggle>

            <PlanGrid>
              {(showMonthly ? monthlyPlans : yearlyPlans).map((plan, idx) => {
                const color = getPlanColor(plan.subscription_number);
                const offers = splitOffersSafely(plan.offers);
                return (
                  <PlanCard key={plan.subscription_number || idx} $color={color}>
                    <h3>{plan.name}</h3>
                    <PlanDescription>
                      {t("loginScreen.plans.description")}
                    </PlanDescription>
                    <PlanPrice>
                      <strong>{plan.monthly_value} €</strong>
                      <span>{t("loginScreen.plans.perMonth")}</span>
                    </PlanPrice>
                    {Number(plan.number_of_days) === 365 && (
                      <DiscountNote>
                        {t("loginScreen.plans.yearlyDiscount")}
                      </DiscountNote>
                    )}

                    <PlanButton
                      type="button"
                      $color={color}
                      onClick={handleSubscribeClick}
                    >
                      {t("loginScreen.plans.subscribe")}
                    </PlanButton>

                    {offers.length > 0 && (
                      <PlanOffers>
                        {offers.map((raw, i) => {
                          const { title, desc } = splitTitleAndDesc(raw);
                          return (
                            <li key={i}>
                              <LuCheck aria-hidden="true" />
                              <div>
                                <strong>{title}</strong>
                                {desc && <span>{desc}</span>}
                              </div>
                            </li>
                          );
                        })}
                      </PlanOffers>
                    )}
                  </PlanCard>
                );
              })}
            </PlanGrid>
          </Inner>
        </Section>
      )}

      <Section
        $flushTop={Boolean(getSubscriptionPackages)}
        aria-labelledby="landing-cta"
      >
        <CtaCard>
          <h2 id="landing-cta">{t("loginScreen.cta.title")}</h2>
          <p>{t("loginScreen.cta.text")}</p>
          <ButtonRow $center>
            <PrimaryButton type="button" onClick={() => openAuth(true)}>
              {t("loginScreen.cta.primary")}
              <LuArrowRight aria-hidden="true" />
            </PrimaryButton>
            {videoButton(t("loginScreen.cta.secondary"))}
          </ButtonRow>
        </CtaCard>
      </Section>
      <VideoModal
        show={showVideo}
        onHide={() => setShowVideo(false)}
        centered
        aria-label={t("loginScreen.video.title")}
      >
        <CloseVideo
          type="button"
          onClick={() => setShowVideo(false)}
          aria-label={t("loginScreen.video.close")}
        >
          <LuX aria-hidden="true" />
        </CloseVideo>
        <VideoPlayer
          autoPlay
          title={t("loginScreen.video.title")}
          playLabel={t("loginScreen.video.play")}
        />
      </VideoModal>
    </Page>
  );
};

export default LoginScreen;
